






































































































































































import { Component, Vue } from 'vue-property-decorator';
import { employeeApis, Employee, InvalidParam } from '@/api';
import { emailApis, EmlAttachment, ImportResult, MailParts, ParsedEml } from '@/api/email.api';
import EmailRichEditor from './EmailRichEditor.vue';
import EmployeeEmailSelect from './EmployeeEmailSelect.vue';

interface ImportForm {
  from_addr: string;
  title: string;
  sent_time: string;
  content: string;
  recipients: string[];
  cc_recipients: string[];
  bcc_recipients: string[];
  import_emails: string[];
  attachments: EmlAttachment[];
  reply_to_message_id: string;
  message_id: string;
  references: string;
}

@Component({
  components: { EmailRichEditor, EmployeeEmailSelect }
})
export default class ImportMailsPage extends Vue {
  parsing = false;
  importing = false;
  emlImported = false;
  confirmVisible = false;
  confirmData: ImportForm | null = null;
  employeeNames: { [email: string]: string } = {};

  form: ImportForm = {
    from_addr: '',
    title: '',
    sent_time: '',
    content: '',
    recipients: [],
    cc_recipients: [],
    bcc_recipients: [],
    import_emails: [],
    attachments: [],
    reply_to_message_id: '',
    message_id: '',
    references: ''
  };

  rules = {
    from_addr: [{ required: true, message: '请输入发件人邮箱', trigger: 'blur' }],
    title: [{ required: true, message: '请输入邮件主题', trigger: 'blur' }],
    sent_time: [{ required: true, message: '请选择发送时间', trigger: 'change' }],
    recipients: [{ required: true, message: '请输入收件人', trigger: 'change' }],
    content: [{ required: true, message: '请输入正文', trigger: 'blur' }],
    import_emails: [{ required: true, message: '请选择需要导入的邮箱', trigger: 'change' }]
  };

  get hasImportEmailPerm() {
    return this.$store.getters['perms/has']('import-email');
  }

  get confirmImportEmailsText() {
    if (!this.confirmData) return '';
    return this.formatRecipients(this.confirmData.import_emails);
  }

  // 邮箱列表转成「姓名 <邮箱>」展示, 查不到员工的邮箱原样显示
  formatRecipients(emails: string[]): string {
    return emails
      .map(email => (this.employeeNames[email] ? `${this.employeeNames[email]} <${email}>` : email))
      .join(', ');
  }

  // 导入邮箱只能从发件人/收件人/抄送/密送里选, 且必须是能查到名字的公司员工
  get importEmailOptions(): string[] {
    const allEmails = new Set<string>(
      [this.form.from_addr, ...this.form.recipients, ...this.form.cc_recipients, ...this.form.bcc_recipients].filter(
        Boolean
      )
    );
    const nameByLower = new Map(Object.entries(this.employeeNames).map(([email, name]) => [email.toLowerCase(), name]));
    return [...allEmails].filter(email => nameByLower.has(email.toLowerCase()));
  }

  formatEmailOption(email: string): string {
    const name = this.employeeNames[email];
    return name ? `${name} <${email}>` : email;
  }

  // 子组件选中的员工合并进来, 供确认弹窗显示姓名
  // 用对象替换而不是新增属性, 否则 Vue2 侦测不到变化, 导入邮箱选项不会刷新
  mergeEmployees(employees: Employee[]) {
    let names = this.employeeNames;
    for (const e of employees) {
      names = { ...names, [e.email]: e.display_name };
    }
    this.employeeNames = names;
  }

  // eml 导入等未经过选择器选中的邮箱, 按邮箱查员工表补齐姓名
  async fillEmployeeNames() {
    const emails = new Set([
      this.form.from_addr,
      ...this.form.recipients,
      ...this.form.cc_recipients,
      ...this.form.bcc_recipients,
      ...this.form.import_emails
    ]);
    const missing = [...emails].filter(e => e && !this.employeeNames[e]);
    await Promise.all(
      missing.map(async email => {
        try {
          const list = await employeeApis.search({ name: email, internal: true });
          const hit = list.find(e => e.email.toLowerCase() === email.toLowerCase());
          if (hit) {
            this.employeeNames = { ...this.employeeNames, [email]: hit.display_name };
          }
        } catch (error) {
          // 查不到姓名不影响导入, 保持原样显示
          console.error(`查询员工 ${email} 失败:`, error);
        }
      })
    );
  }

  // 参数校验失败的详细内容在后端返回的 params 里, message 只有 invalid_param
  errorMessage(error: any): string {
    if (error instanceof InvalidParam && error.params) {
      const details = Object.values(error.params)
        .map(v => String(v))
        .filter(Boolean)
        .join('；');
      if (details) return details;
    }
    return `${error}`;
  }

  // 统一格式化为东八区标准 ISO 字符串(带冒号偏移, 与后端 isoformat 输出一致), picker 展示即东八区, 传输携带时区
  formatCst(date: Date): string {
    const cst = new Date(date.getTime() + 8 * 3600 * 1000);
    const pad = (n: number) => String(n).padStart(2, '0');
    return (
      `${cst.getUTCFullYear()}-${pad(cst.getUTCMonth() + 1)}-${pad(cst.getUTCDate())}` +
      `T${pad(cst.getUTCHours())}:${pad(cst.getUTCMinutes())}:${pad(cst.getUTCSeconds())}+08:00`
    );
  }

  onSentTimeChange(val: string) {
    // fecha 解析时忽略时区偏移, 因此 v-model 值必须是东八区表示, 否则展示会差 8 小时
    const date = new Date(val);
    if (!isNaN(date.getTime())) {
      this.form.sent_time = this.formatCst(date);
    }
  }

  // 确认弹窗按东八区展示
  formatSentTimeDisplay(value: string): string {
    const date = new Date(value);
    if (isNaN(date.getTime())) return value;
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(
      date.getMinutes()
    )}`;
  }

  async handleEmlChange(file: { raw: File }) {
    if (!file.raw) return;
    this.parsing = true;
    try {
      const parts: ParsedEml = await emailApis.parseImportEml(file.raw);
      this.applyMailParts(parts);
      this.emlImported = true;
      this.$message.success('eml 解析成功，已自动填充表单');
    } catch (error) {
      this.$message.error(`eml 解析失败: ${this.errorMessage(error)}`);
      console.error(error);
    } finally {
      this.parsing = false;
    }
  }

  applyMailParts(parts: ParsedEml) {
    this.form = {
      from_addr: parts.from_addr,
      title: parts.title,
      sent_time: parts.sent_time,
      content: parts.content,
      recipients: parts.recipients,
      cc_recipients: parts.cc_recipients,
      bcc_recipients: parts.bcc_recipients,
      import_emails: this.form.import_emails,
      attachments: parts.attachments,
      // 原邮件的 In-Reply-To 就是被回复邮件的 Message-ID
      reply_to_message_id: parts.in_reply_to || '',
      // 保留原 Message-ID 和完整 References 链, 导入后多封邮件才能串成会话
      message_id: parts.message_id || '',
      references: parts.references || ''
    };
  }

  async handleImport() {
    const form = this.$refs.importForm as any;
    form.validate(async (valid: boolean) => {
      if (!valid) return;
      // 先补齐未经过选择器选中的邮箱姓名, 再弹出完整内容让用户二次确认
      await this.fillEmployeeNames();
      this.confirmData = { ...this.form };
      this.confirmVisible = true;
    });
  }

  async doImport() {
    if (!this.confirmData) return;

    const mailParts: MailParts = {
      from_addr: this.confirmData.from_addr,
      title: this.confirmData.title,
      sent_time: this.confirmData.sent_time,
      content: this.confirmData.content,
      recipients: this.confirmData.recipients,
      cc_recipients: this.confirmData.cc_recipients,
      bcc_recipients: this.confirmData.bcc_recipients,
      attachments: this.confirmData.attachments,
      reply_to_message_id: this.confirmData.reply_to_message_id || null,
      message_id: this.confirmData.message_id || null,
      references: this.confirmData.references || null
    };
    if (this.confirmData.import_emails.length === 0) {
      this.$message.error('请选择需要导入的邮箱');
      return;
    }

    this.importing = true;
    try {
      const result: ImportResult = await emailApis.importMails({
        mail_parts: mailParts,
        import_emails: this.confirmData.import_emails
      });
      this.confirmVisible = false;
      if (result.failed.length > 0) {
        this.$message.warning(
          `导入完成：成功 ${result.imported.length} 个，失败 ${result.failed.length} 个（${result.failed.join(', ')}）`
        );
      } else {
        this.$message.success(`导入成功：${result.imported.join(', ')}`);
      }
      this.resetForm();
    } catch (error) {
      this.$message.error(`导入失败: ${this.errorMessage(error)}`);
      console.error(error);
    } finally {
      this.importing = false;
    }
  }

  resetForm() {
    this.emlImported = false;
    this.form = {
      from_addr: '',
      title: '',
      sent_time: '',
      content: '',
      recipients: [],
      cc_recipients: [],
      bcc_recipients: [],
      import_emails: [],
      attachments: [],
      reply_to_message_id: '',
      message_id: '',
      references: ''
    };
    this.employeeNames = {};
    this.confirmData = null;
    const form = this.$refs.importForm as any;
    if (form && form.clearValidate) {
      form.clearValidate();
    }
  }
}
